import api from './api';

/**
 * REPORTING-CENTER.1 — client for the date-range Inventory Movements & Stock
 * on Hand report.
 *
 * GET /admin/venue/:venueId/inventory/reports/movements
 * Backend contract: nota-wine-api inventoryMovementsReportController/Service.
 */

const base = (venueId: string) => `/admin/venue/${venueId}/inventory/reports`;

/**
 * REPORTING.R5 — one location's share of a row.
 *
 * The same shape as the row it belongs to, and the entries sum to it: a
 * decomposition, not a second opinion. Quantities only — the server does not
 * split money per location, so the report's money columns stay blank on a
 * breakdown line rather than showing a figure that was never calculated.
 */
export interface InventoryMovementsReportRowLocation {
  locationId: string | null;
  /** Warehouse-qualified, e.g. "Alfred's · Racks". Null only for a null locationId. */
  locationName: string | null;
  openingQuantity: number;
  received: number;
  transferIn: number;
  adjustmentPlus: number;
  salesDepletion: number;
  waste: number;
  transferOut: number;
  adjustmentMinus: number;
  reversalVoid: number;
  netChange: number;
  closingQuantity: number;
}

export interface InventoryMovementsReportRow {
  inventoryItemId: string;
  shortId: string | null;
  itemName: string | null;
  itemType: string | null;
  category: string;
  subcategory: string;
  sizeValue: number | null;
  sizeUnit: string | null;
  /** HOTFIX-UI-3 (fix 4) — item lastSupplierName. Operational: never cost-stripped. */
  supplierName: string | null;
  /** HOTFIX-UI-3 (fix 4) — item salePrice, MINOR units. Server-stripped without stock-costs:view. */
  salePrice: number | null;
  /**
   * REPORTING.R5 — where this row's movement actually happened.
   *
   * Replaces the row's old `locationName`, which was the FILTER's location
   * repeated on every row and never the row's own. Always present and never
   * empty: a row exists because something moved somewhere.
   */
  byLocation: InventoryMovementsReportRowLocation[];
  openingQuantity: number;
  received: number;
  transferIn: number;
  adjustmentPlus: number;
  salesDepletion: number;
  waste: number;
  transferOut: number;
  adjustmentMinus: number;
  reversalVoid: number;
  netChange: number;
  closingQuantity: number;
  unitCost: number | null;
  openingValue: number | null;
  closingValue: number | null;
}

export interface InventoryMovementsReportMovement {
  date: string;
  inventoryItemId: string;
  shortId: string | null;
  itemName: string | null;
  location: string | null;
  movementType: string;
  quantity: number;
  unitCost: number | null;
  totalCost: number | null;
  costBasis: string | null; // COSTING.1d — per-row recorded/estimated basis
  bucket: string | null;
  sourceDocumentType: string | null;
  sourceDocumentId: string | null;
  /** POS-ORDER-NUMBER.1 — human-readable; null on most rows. See MovementSourceDocument. */
  sourceDocumentNumber: string | null;
  postedBy: string | null;
  notes: string | null;
}

export interface InventoryMovementsReportMeta {
  reportName: string;
  venueId: string;
  venueName: string | null;
  from: string;
  to: string;
  filters: {
    locationId: string | null;
    categoryId: string | null;
    subcategoryId: string | null;
    itemId: string | null;
    includeMovements: boolean;
  };
  locationName: string | null;
  openingBasis: 'LEDGER_SUM';
  costBasis: 'CURRENT_LAST_COST';
  movementCount: number;
  truncated: boolean;
  generatedAt: string;
  notes: string[];
}

export interface InventoryMovementsReport {
  meta: InventoryMovementsReportMeta;
  rows: InventoryMovementsReportRow[];
  movements?: InventoryMovementsReportMovement[];
}

export interface InventoryMovementsReportParams {
  /** Required ISO date (period start, inclusive; opening is everything before it). */
  from: string;
  /** Required ISO date (period end, inclusive). */
  to: string;
  locationId?: string;
  categoryId?: string;
  subcategoryId?: string;
  itemId?: string;
  /** Default true; false omits the movement-detail rows. */
  includeMovements?: boolean;
}

// ── REPORTING-CENTER.2 — Inventory Transaction Register / Stock Card ──────────

export interface TransactionRegisterRow {
  movementId: string;
  date: string;
  inventoryItemId: string;
  shortId: string | null;
  itemName: string | null;
  itemType: string | null;
  category: string;
  subcategory: string;
  sizeValue: number | null;
  sizeUnit: string | null;
  locationId: string | null;
  locationName: string | null;
  movementType: string;
  bucket: string | null;
  qtyIn: number;
  qtyOut: number;
  quantity: number;
  /** Stock-card mode only. */
  runningBalance?: number;
  // REPORTING-CENTER.2.3 — interim money columns, CURRENT_LAST_COST basis, all
  // MINOR units (convert with fromMinorUnits at display/export only).
  // REGISTER-REVENUE.1 — cost only. `salePrice` and `estRevenue` are gone: this
  // register reports COMPONENT movements, so a recipe sale posts one row per
  // component and the item the guest bought has no row at all. Revenue belongs
  // to the line, never to a component of it.
  unitCost: number | null;
  value: number | null;
  recordedUnitCost: number | null;
  recordedTotalCost: number | null;
  costBasis: string | null; // COSTING.1d — per-row recorded/estimated basis
  sourceDocumentType: string | null;
  sourceDocumentId: string | null;
  /**
   * POS-ORDER-NUMBER.1 — the human-readable order number, DISPLAY only.
   * Null for anything posted before the sender included it, and for source
   * documents that have no human number at all (stock count, transfer and goods
   * receipt are ObjectId-identified). Fall back to the id, never to blank.
   */
  sourceDocumentNumber: string | null;
  postedBy: string | null;
  notes: string | null;
}

export interface TransactionRegisterMeta {
  reportName: string;
  venueId: string;
  venueName: string | null;
  from: string;
  to: string;
  filters: {
    locationId: string | null;
    types: string[] | null;
    categoryId: string | null;
    subcategoryId: string | null;
    itemType: string | null;
    itemId: string | null;
    q: string | null;
  };
  locationName: string | null;
  mode: 'REGISTER' | 'STOCK_CARD';
  /** REPORTING-CENTER.2.3 — interim value basis for every money column. */
  costBasis?: 'CURRENT_LAST_COST';
  stockCard?: {
    itemId: string; itemName: string | null;
    openingBalance: number; closingBalance: number;
    /** MINOR units; balance × current unit cost. Null when no cost basis. */
    openingValue: number | null; closingValue: number | null;
  };
  total: number;
  truncated: boolean;
  generatedAt: string;
  notes: string[];
}

export interface TransactionRegisterPagination {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

export interface TransactionRegisterResponse {
  meta: TransactionRegisterMeta;
  rows: TransactionRegisterRow[];
  pagination?: TransactionRegisterPagination;
}

export interface TransactionRegisterParams {
  from: string;
  to: string;
  locationId?: string;
  types?: string[];
  categoryId?: string;
  subcategoryId?: string;
  itemType?: string;
  itemId?: string;
  q?: string;
  page?: number;
  pageSize?: number;
  /** Export mode — full filtered set up to the server cap, no pagination. */
  export?: boolean;
}

function buildRegisterParams(params: TransactionRegisterParams): Record<string, unknown> {
  const p: Record<string, unknown> = { from: params.from, to: params.to };
  if (params.locationId) p.locationId = params.locationId;
  if (params.types && params.types.length > 0) p.type = params.types.join(',');
  if (params.categoryId) p.categoryId = params.categoryId;
  if (params.subcategoryId) p.subcategoryId = params.subcategoryId;
  if (params.itemType) p.itemType = params.itemType;
  if (params.itemId) p.itemId = params.itemId;
  if (params.q) p.q = params.q;
  if (params.export) p.export = 'true';
  if (params.page) p.page = params.page;
  if (params.pageSize) p.pageSize = params.pageSize;
  return p;
}

/**
 * REPORTING.R6 — who consumed a warehouse's stock.
 *
 * The venue in the path is the READER; the venues in the response are whoever
 * consumed the stock. Quantities are POSITIVE consumption (a reversal subtracts)
 * and `cost` is minor units, absent entirely for a caller without
 * stock-costs:view — absent rather than 0, because a 0 would read as "consumed
 * nothing of value".
 */
export interface WarehouseConsumptionItem {
  inventoryItemId: string;
  itemName: string | null;
  quantity: number;
  cost?: number | null;
}

export interface WarehouseConsumptionVenue {
  venueId: string;
  venueName: string | null;
  /** The venue that administers the warehouse, so it can read differently. */
  isHomeVenue: boolean;
  quantity: number;
  cost?: number | null;
  items: WarehouseConsumptionItem[];
}

export interface WarehouseConsumptionReport {
  meta: {
    reportName: string;
    warehouseId: string;
    warehouseName: string | null;
    homeVenueId: string | null;
    from: string;
    to: string;
    movementCount: number;
    /**
     * Depletion the report could NOT attribute to a venue. Surfaced in the page,
     * not buried: a reader comparing this to the Movements report will find less
     * depletion here and needs to see why.
     */
    excludedMovementCount: number;
    generatedAt: string;
    notes: string[];
  };
  byVenue: WarehouseConsumptionVenue[];
  totals: { quantity: number; cost?: number | null };
}

export interface WarehouseConsumptionParams {
  warehouseId: string;
  from: string;
  to: string;
}

const inventoryReportService = {
  getMovementsReport: async (
    venueId: string,
    params: InventoryMovementsReportParams,
  ): Promise<InventoryMovementsReport> => {
    const p: Record<string, unknown> = { from: params.from, to: params.to };
    if (params.locationId) p.locationId = params.locationId;
    if (params.categoryId) p.categoryId = params.categoryId;
    if (params.subcategoryId) p.subcategoryId = params.subcategoryId;
    if (params.itemId) p.itemId = params.itemId;
    if (params.includeMovements === false) p.includeMovements = 'false';
    const response = await api.get(`${base(venueId)}/movements`, { params: p });
    return response.data;
  },

  /** REPORTING.R6 — consumption of one warehouse, grouped by the venue that consumed it. */
  getWarehouseConsumption: async (
    venueId: string,
    params: WarehouseConsumptionParams,
  ): Promise<WarehouseConsumptionReport> => {
    const response = await api.get(`${base(venueId)}/warehouse-consumption`, {
      params: { warehouseId: params.warehouseId, from: params.from, to: params.to },
    });
    return response.data;
  },

  /** Paginated transaction register (or stock card when itemId is set). */
  getTransactionRegister: async (
    venueId: string,
    params: TransactionRegisterParams,
  ): Promise<TransactionRegisterResponse> => {
    const response = await api.get(`${base(venueId)}/transactions`, { params: buildRegisterParams(params) });
    return response.data;
  },
};

export default inventoryReportService;
